import { useCallback, useEffect, useState } from 'react';
import { fetchPaymentDetail, parseApiError } from './api';
import type { PaymentDetailResponse } from './types';

/**
 * Loads one payment's detail by id on entry (PRD §2/§7). Self-sufficient: a
 * deep link or refresh of `detail/:revenueId` re-fetches cleanly.
 *
 * Error model (PRD §9): any non-2xx enters the `error` phase; the `errorCode`
 * is surfaced so the screen can distinguish `PAYMENT_NOT_FOUND` (404) and
 * `FORBIDDEN` (403, non-retryable) from a retryable `UPSTREAM_FAILURE` (502).
 */
export type DetailPhase = 'loading' | 'ready' | 'error';

export type PaymentDetailState = {
  phase: DetailPhase;
  data: PaymentDetailResponse | null;
  errorCode: string | null;
  reload: () => void;
};

export const usePaymentDetail = (revenueId: string): PaymentDetailState => {
  const [phase, setPhase] = useState<DetailPhase>('loading');
  const [data, setData] = useState<PaymentDetailResponse | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setPhase('loading');
    setErrorCode(null);
    try {
      const res = await fetchPaymentDetail(revenueId);
      setData(res);
      setPhase('ready');
    } catch (error) {
      setData(null);
      setErrorCode(parseApiError(error).errorCode);
      setPhase('error');
    }
  }, [revenueId]);

  useEffect(() => {
    void load();
  }, [load]);

  return { phase, data, errorCode, reload: () => void load() };
};
